import { motion } from 'framer-motion'
import { Wifi, Smartphone, Trophy, Settings, ArrowRight } from 'lucide-react'

const features = [
  {
    icon: Wifi,
    tag: '物联网云跑步机',
    title: '扫码即跑，在线预约',
    desc: '无人值守，自助服务',
    color: '#8b5cf6',
    bg: 'rgba(139,92,246,0.08)',
    border: 'rgba(139,92,246,0.2)',
  },
  {
    icon: Smartphone,
    tag: 'Lafit智能健身',
    title: '云端健身指导',
    desc: '实时配速曲线，科学健身',
    color: '#a78bfa',
    bg: 'rgba(139,92,246,0.08)',
    border: 'rgba(139,92,246,0.2)',
  },
  {
    icon: Trophy,
    tag: 'Lafit独有模式',
    title: '云端控速，社区竞赛',
    desc: '多种赛道，多级难度，更多乐趣',
    color: '#c4b5fd',
    bg: 'rgba(139,92,246,0.08)',
    border: 'rgba(139,92,246,0.2)',
  },
  {
    icon: Settings,
    tag: 'Lafit提升运维效率',
    title: '自动上线/下线',
    desc: '云端管理，厂家维护',
    color: '#8b5cf6',
    bg: 'rgba(139,92,246,0.08)',
    border: 'rgba(139,92,246,0.2)',
  },
]

export default function Treadmill() {
  return (
    <div className="min-h-screen pt-16">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] opacity-10"
          style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.6), transparent 70%)', filter: 'blur(60px)' }} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <img src="https://sh.lafit.club/images/Lafit%20Logo%20FC6223%20Square.png" alt="Lafit" className="w-20 h-20 rounded-2xl object-contain" />
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tight">
              物联网云跑步机
            </h1>
            <p className="text-xl text-gray-400 mb-10">
              扫码即跑，在线预约，无人值守，自助服务
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://sh.lafit.club/price.html" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-white transition-all"
                style={{ background: '#8b5cf6', boxShadow: '0 0 30px rgba(139,92,246,0.4)' }}>
                了解价格 <ArrowRight className="w-4 h-4" />
              </a>
              <a href="https://sh.lafit.club/shop-information.html" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-purple-300 transition-all"
                style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)' }}>
                门店信息 <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Smart Fitness ───────────────────────────────────────────────── */}
      <section className="py-20 relative" style={{ background: 'linear-gradient(180deg, rgba(139,92,246,0.06) 0%, transparent 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Lafit智能健身</h2>
            <p className="text-xl text-gray-400 mb-8">云端健身指导，实时配速曲线，科学健身</p>

            {/* WeChat + Mini Program */}
            <div className="flex flex-wrap justify-center gap-8">
              <div className="flex flex-col items-center gap-2">
                <img
                  src="https://www.lafit.club/images/LaFit_INT_wechat.png"
                  alt="微信公众号"
                  className="w-36 h-36 rounded-xl object-contain border"
                  style={{ borderColor: 'rgba(139,92,246,0.3)' }}
                />
                <span className="text-sm text-gray-400">关注微信</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <img
                  src="https://lafit.oss-cn-beijing.aliyuncs.com/image/gh_d57265bd39be_860.jpg"
                  alt="小程序"
                  className="w-36 h-36 rounded-xl object-contain border"
                  style={{ borderColor: 'rgba(139,92,246,0.3)' }}
                />
                <span className="text-sm text-gray-400">小程序</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Four Feature Sections ────────────────────────────────────────── */}
      {features.map((f, i) => (
        <section key={f.tag} className={`py-20 relative ${i % 2 === 1 ? '' : ''}`}
          style={{ background: i % 2 === 0 ? 'rgba(0,0,0,0.15)' : 'transparent' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`flex flex-col md:flex-row items-center gap-12 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              {/* Visual card */}
              <motion.div
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="flex-1 max-w-md w-full"
              >
                <div className="rounded-2xl p-8 text-center"
                  style={{ background: f.bg, border: `1px solid ${f.border}` }}>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'rgba(139,92,246,0.15)' }}>
                    <f.icon className="w-8 h-8" style={{ color: f.color }} />
                  </div>
                  <p className="text-xs uppercase tracking-widest mb-2" style={{ color: f.color }}>{f.tag}</p>
                  <h3 className="text-2xl font-black text-white mb-2">{f.title}</h3>
                  <p className="text-gray-400">{f.desc}</p>
                </div>
              </motion.div>

              {/* Text side */}
              <motion.div
                initial={{ opacity: 0, x: i % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4"
                  style={{ background: f.bg, color: f.color, border: `1px solid ${f.border}` }}>
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: f.color }} />
                  {f.tag}
                </div>
                <h3 className="text-3xl font-black text-white mb-4">{f.title}</h3>
                <p className="text-gray-400 text-lg leading-relaxed">{f.desc}</p>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-16 text-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(59,130,246,0.08))' }}>
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-black text-white mb-4">AI 赋能，让跑步更聪明</h2>
            <p className="text-gray-400 mb-8">了解更多智能跑步机产品信息</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://sh.lafit.club/price.html" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all"
                style={{ background: '#8b5cf6', boxShadow: '0 0 30px rgba(139,92,246,0.4)' }}>
                了解价格 <ArrowRight className="w-4 h-4" />
              </a>
              <a href="https://sh.lafit.club" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-purple-300 transition-all"
                style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)' }}>
                访问官网 <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
